import * as React from "react";

import { Banner } from "@/components/ui/banner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { formatDate } from "@/lib/format-date";
import type { AdminOrgDetail } from "@/hooks/queries/admin-orgs-types";

// Story 15.6 AC23 — Screen 24's Contract card, and the HOST of the unit's two
// named control slots.
//
// THE SLOT CONTRACT IS PINNED AND IS NOT A COMPANION'S TO CHANGE. Three
// optional `ReactNode` props, all defaulting to `null`:
//
//   | prop                  | wrapper attribute                        | filled by |
//   |-----------------------|------------------------------------------|-----------|
//   | `seatControl`         | `data-slot="seat-capacity-control"`      | 15-2      |
//   | `contractDateControl` | `data-slot="contract-end-date-control"`  | 15-3      |
//   | `renewControl`        | `data-slot="renew-contract-control"`     | 21-1      |
//
// rendered in THAT order, inside the one card element. The PROP is what a
// companion passes at the `OrgDetailScreen.tsx` call site; the ATTRIBUTE is what
// every test — this story's AC23 and each companion's own mount test — selects
// on. Story 15-3 is already authored against `contractDateControl`, so no name
// above is available to rename.
//
// THE THIRD SLOT IS STORY 21.1's OWN CHANGE TO THIS CONTRACT, not a companion
// editing the host: 21.1 ships the slot AND its mount in one diff, which is why
// it is not registered as a seam (unlike `seam-156`'s two slots, which were
// split across stories). The rule the paragraph below states — a companion adds
// a prop at the call site and never opens this file — is unchanged by that, and
// is what a future companion is still held to.
//
// The renew slot goes LAST on purpose. The two above it are mid-term
// corrections an operator reaches for repeatedly; a term boundary is the one
// write on this card that cannot be undone through any endpoint, so it does not
// sit between them.
//
// A companion fills its slot by passing its prop at that call site — one import,
// one prop. IT NEVER EDITS THIS FILE, which is what stops two companions
// colliding inside one component, and it never renames or removes a `data-slot`.
//
// A NULL PROP RENDERS NOTHING — not the wrapper, not an empty element, not a
// disabled button, not a "coming soon" affordance. Two rejections, both recorded:
// a disabled `Adjust seat capacity` button invites a support ticket about a
// button that does nothing, and a persistent empty `<div data-slot=…>` is an
// element that exists only to be found by a selector, which makes every
// presence check pass on a card with no controls at all.
//
// THE MOCKUP'S "Stripe + seats" INFO BANNER IS RENDERED HERE, immediately above
// the seat slot (product ruling, 2026-08-26; DW-386). Screen 23 draws it at the
// top of the DIRECTORY
// (`docs/screens/amps-screen-23-platform-organizations.html:54-56`), but a
// sentence explaining a hard stop belongs where the operator acts on it, and the
// only control that changes the pool is the one in `seatControl` below.
//
// It is NOT rendered on the directory, and Story 15.6 AC30 asserts that absence.
// That stays true: this is Screen 24's Contract card, a different surface.
//
// It is rendered UNCONDITIONALLY, not gated on `seatControl`. The hard stop is a
// property of the ORGANIZATION, not of this operator's controls — a read-only
// card still has to explain why "Near seat limit" matters — and a banner that
// appears only when a control happens to be mounted is a sentence nobody can
// rely on having read.

export interface ContractCardProps {
  /** The organization record this card reports on. */
  organization: AdminOrgDetail;

  /**
   * SLOT 1 (`slot:seat-capacity-control`) — Story 15.2's adjust-seat-capacity
   * control. Null renders neither the control nor its wrapper. PINNED NAME.
   */
  seatControl?: React.ReactNode;

  /**
   * SLOT 2 (`slot:contract-end-date-control`) — Story 15.3's
   * extend-contract-end-date control. Same null behaviour. PINNED NAME.
   */
  contractDateControl?: React.ReactNode;

  /**
   * SLOT 3 (`slot:renew-contract-control`) — Story 21.1's term-boundary
   * control. Same null behaviour. PINNED NAME.
   */
  renewControl?: React.ReactNode;

  /**
   * Draft value of the write-once External reference ID control. Omit it (with
   * `onExternalReferenceIdChange`) to render the card read-only — which is what a
   * direct-drive test does, and what a caller with no write path should do.
   */
  externalReferenceId?: string;
  onExternalReferenceIdChange?: (value: string) => void;
  externalReferenceIdError?: string;

  /** Draft value of the editable contract START date (AC9 — the DW-67 second half). */
  contractStartDate?: string;
  onContractStartDateChange?: (value: string) => void;
  contractStartDateError?: string;
}

/**
 * What renewing IS on this record — and what extending is not (Story 21.1 AC28).
 *
 * The superseded wording was *"Extend the end date on this record to renew — no
 * rollover, no credits, no proration."* It described the only write that existed
 * when Screen 24 shipped, and FR87 made it false: extending moves one date
 * inside the current term and touches neither the term boundary nor the seat
 * pool, while renewing closes a term, opens the next and sets the seats it
 * starts with.
 *
 * The **"no rollover, no credits, no proration"** clause survives VERBATIM.
 * NFR40/AR54 are untouched by this story — a renewal still carries nothing over
 * and bills nothing back — and that half is the one an operator checks the card
 * for.
 *
 * It says what each control does and stops there: the wording drift this exact
 * pair already suffered once is why `ExtendContractControl` may not restate it
 * (W3's one-copy-per-fact rule), and the same rule keeps the seat arithmetic off
 * this line — the dialog states it where the operator is about to act on it.
 */
export const CONTRACT_RENEWAL_SENTENCE =
  "Renewing opens a new term and sets the seats it starts with — no rollover, no credits, no proration. Extending the end date is a mid-term correction and does neither.";

/**
 * The "Stripe + seats" banner, transcribed from Screen 23
 * (`amps-screen-23-platform-organizations.html:54-56`) rather than rewritten —
 * the mockup is client-validated, and "few seats remain before that hard stop"
 * is the phrase that ties the Status column's `Near seat limit` flag to the
 * refusal an operator will otherwise meet without warning.
 *
 * `Near seat limit` is the LABEL the shipped derivation produces
 * (`admin-orgs-derivations.ts :: orgStatusPills`, the seat axis) — if that
 * string ever moves, this sentence points at a flag nobody sees. Story 15.7
 * split that derivation into a contract axis and a seat axis; the label is
 * unchanged, and it is now reachable on a row whose contract is also flagged,
 * which is exactly the case this banner warns about.
 */
export const STRIPE_SEATS_BANNER_TITLE = "Stripe + seats.";

/** The "you get one shot at this" helper beside the External reference ID input. */
export const EXTERNAL_REFERENCE_WRITE_ONCE_HELPER =
  "The external reference ID can be set once. After it is saved it cannot be changed from this screen.";

/** Read-only rows use the same two-column shape as the sibling admin detail screen. */
function ReadOnlyRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-2">
      <span className="text-sm font-semibold text-[color:var(--text-primary)]">{label}</span>
      <span className="text-sm text-[color:var(--text-secondary)] [font-variant-numeric:tabular-nums]">
        {value}
      </span>
    </div>
  );
}

export function ContractCard({
  organization,
  // Both slots default to `null` — the state THIS story's own diff ships, since
  // 15.2 and 15.3 pass their props at the call site later in this same unit.
  seatControl = null,
  contractDateControl = null,
  renewControl = null,
  externalReferenceId,
  onExternalReferenceIdChange,
  externalReferenceIdError,
  contractStartDate,
  onContractStartDateChange,
  contractStartDateError,
}: ContractCardProps) {
  // WRITE-ONCE, MIRRORED FROM THE SERVER — never replacing it (AC26). The control
  // is an input only while the STORED value is null; once set it is read-only
  // text. The server's `409 ORG-018` is still the authority and still renders
  // inline on this field, because two operators can be on this screen at once and
  // only one of them can win.
  const referenceIsSet = organization.external_reference_id != null;
  const canEditReference = !referenceIsSet && onExternalReferenceIdChange != null;
  const canEditStartDate = onContractStartDateChange != null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Contract</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          {canEditStartDate ? (
            <FormField
              label="Start date"
              htmlFor="org-contract-start-date"
              error={contractStartDateError}
              // The one date whose absence is unrecoverable elsewhere: the
              // contract guard fails closed on a NULL start, so an organization
              // created without one is write-locked until it is set HERE.
              helper="A contract with no start date cannot be extended later."
            >
              <Input
                id="org-contract-start-date"
                type="date"
                value={contractStartDate ?? ""}
                onChange={(event) => onContractStartDateChange(event.target.value)}
              />
            </FormField>
          ) : (
            <ReadOnlyRow
              label="Start date"
              value={
                organization.contract_start_date
                  ? formatDate(organization.contract_start_date)
                  : "Not set"
              }
            />
          )}

          {/* CURRENT TERM STARTED — Story 21.1 AC25. Beside `Start date`, never
              instead of it: the two answer different questions and only diverge
              after the first renewal, which is exactly when an operator most
              needs to see both. The field is `current_term_started_at`, whose
              NULL→`contract_start_date` fallback the SERVER applies (AC2), so
              nothing here re-derives it and disagrees.

              `Start date` above keeps its meaning — the relationship start,
              unchanged by every renewal — and its editability is untouched. */}
          <ReadOnlyRow
            label="Current term started"
            value={
              organization.current_term_started_at
                ? formatDate(organization.current_term_started_at)
                : "Not set"
            }
          />

          {/* END DATE IS READ-ONLY HERE, ALWAYS. Two endpoints write it —
              `extend_contract` mid-term (AC10) and 21.1's `renew_contract` at a
              term boundary — and neither is reached from this card's own body:
              each has its own dialog, in the second and third slots below. */}
          <ReadOnlyRow
            label="End date"
            value={
              organization.contract_end_date
                ? formatDate(organization.contract_end_date)
                : "Not set"
            }
          />
        </div>

        <p className="text-sm text-[color:var(--text-tertiary)]">{CONTRACT_RENEWAL_SENTENCE}</p>

        {canEditReference ? (
          <FormField
            label="External reference ID"
            htmlFor="org-external-reference-id"
            error={externalReferenceIdError}
            helper={EXTERNAL_REFERENCE_WRITE_ONCE_HELPER}
          >
            <Input
              id="org-external-reference-id"
              autoComplete="off"
              placeholder="CRM key supplied by CITS"
              value={externalReferenceId ?? ""}
              onChange={(event) => onExternalReferenceIdChange(event.target.value)}
            />
          </FormField>
        ) : (
          <div className="flex flex-col gap-[6px]">
            <ReadOnlyRow
              label="External reference ID"
              value={organization.external_reference_id ?? "Not set"}
            />
            {referenceIsSet ? (
              <p className="text-sm text-[color:var(--text-tertiary)]">
                {EXTERNAL_REFERENCE_WRITE_ONCE_HELPER}
              </p>
            ) : null}
            {/* A direct ORG-018 still lands on this field even when the control
                has already gone read-only — the refusal and the mirror can
                disagree, and the server is the one that decides. */}
            {externalReferenceIdError ? (
              <p role="alert" className="text-sm text-[color:var(--text-error)]">
                {externalReferenceIdError}
              </p>
            ) : null}
          </div>
        )}

        {/* The seat hard stop, next to the control that changes the pool. */}
        <Banner variant="info" title={STRIPE_SEATS_BANNER_TITLE}>
          Assigned seats cannot exceed entitled seats — the product blocks invites and
          provisioning when the pool is full. Flags like <em>Near seat limit</em> mean few seats
          remain before that hard stop.
        </Banner>

        {/* SLOT 1 — 15.2's adjust-seat-capacity control. */}
        {seatControl ? <div data-slot="seat-capacity-control">{seatControl}</div> : null}

        {/* SLOT 2 — 15.3's extend-contract-end-date control. */}
        {contractDateControl ? (
          <div data-slot="contract-end-date-control">{contractDateControl}</div>
        ) : null}

        {/* SLOT 3 — 21.1's renew-contract control. */}
        {renewControl ? <div data-slot="renew-contract-control">{renewControl}</div> : null}
      </CardContent>
    </Card>
  );
}

export default ContractCard;
